import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';
import { login as apiLogin, getCurrentUser, User } from '../services/api';

// Define types
// (Removed User interface as it's now imported from api.ts)

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  token: string | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
}

// Create the context
export const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  token: null,
  login: async () => false,
  logout: () => {},
});

// Create the auth provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // On mount, check if user is already logged in
  useEffect(() => {
    const verifySession = async () => {
      try {
        const storedToken = localStorage.getItem('token');
        
        if (!storedToken) {
          setLoading(false);
          return;
        }
        
        // Set token in state and axios headers
        setToken(storedToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
        
        // Verify the token by fetching current user
        const currentUser = await getCurrentUser();
        
        // If we get here, the token is valid
        setUser(currentUser);
        setIsAuthenticated(true);
        
        // Update user in localStorage for persistence
        localStorage.setItem('user', JSON.stringify(currentUser));
      } catch (error) {
        // Token is invalid, clear auth state
        console.error('Session verification failed:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setToken(null);
        setUser(null);
        setIsAuthenticated(false);
        delete axios.defaults.headers.common['Authorization'];
      } finally {
        setLoading(false);
      }
    };
    
    verifySession();
  }, []);
  
  // Login function
  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      const { token, user } = await apiLogin(username, password);
      
      // Save to state
      setToken(token);
      setUser(user);
      setIsAuthenticated(true);
      
      // Save to localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      // Set axios default headers
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      return true;
    } catch (error) {
      console.error('Login failed:', error);
      return false;
    }
  };
  
  // Logout function
  const logout = () => {
    // Clear state
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    
    // Clear localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    // Clear axios default headers
    delete axios.defaults.headers.common['Authorization'];
  };
  
  if (loading) {
    // Return a loading state or spinner while verifying the session
    return <div>Loading...</div>;
  }
  
  return (
    <AuthContext.Provider value={{ user, isAuthenticated, token, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}; 